import { FiClock, FiMapPin, FiUsers } from "react-icons/fi";
import Buttons from "../../../../components/button";
import {
  HStack,
  VStack,
} from "../../../../components/composition/stack.layout";
import DateTime from "../../../../components/datetime";
import { DotDivider } from "../../../../components/dividers";
import { HorizontalList } from "../../../../components/horizontal-list/horizontal-list";

import { RichTextEditorBase } from "../../../../components/rich-text-editor/rich-text-editor-base";
import { toValidLuxonDateTime } from "@/components/utils/dates/to-luxon-datetime";
import { useServerTimeDeadline } from "@/components/utils/useServerTime";
import { Tooltip } from "@/flokit/core/tooltip/tooltip";
import { isCurrentDateAfterStartDate } from "@/utils/datetime-utils";

export const BaseJobDetailsPane = (props: {
  title: string;
  description: string;
  offices?: {
    name?: string;
  }[];
  positionTypes?: {
    name?: string;
  }[];
  departments?: {
    name?: string;
  }[];
  closeDate?: string;
  openDate?: string;

  allowRequestApplication?: boolean;
  onRequestApplication: () => void;
  onCopyJobLink: () => void;
  applyButtonDisabled?: boolean;
  isUpdate?: boolean;
}) => {
  const closeDateDeadline = useServerTimeDeadline({
    until: toValidLuxonDateTime(props.closeDate),
    enabled: !!props.closeDate,
  });

  function isApplyButtonDisabled() {
    if (props.isUpdate) {
      return false;
    }
    return props.applyButtonDisabled || closeDateDeadline === "after";
  }

  return (
    <div className="mt-6 w-full rounded-xl bg-white p-8 shadow-md">
      <VStack className={"w-full"}>
        <div className="font-inter pb-2 text-4xl font-semibold leading-none">
          {props?.title}
        </div>
        <VStack
          className={
            "border-b-dublin-gray flex w-full flex-col gap-y-2 border-b pb-7 pt-3"
          }
        >
          <HStack className={"items-center space-x-4"}>
            <FiMapPin className={"text-macbook flex-shrink-0"} />
            <HorizontalList
              ariaLabel="Offices"
              items={props?.offices?.map((office, index) => {
                return (
                  <HStack key={index} className={"space-x-4"}>
                    <div className={"font-regular text-base"}>
                      {office.name}
                    </div>
                  </HStack>
                );
              })}
            />
          </HStack>
          <HStack className={"items-center space-x-4"}>
            <img
              alt={"suitcase"}
              src={"/v2/suitcase.svg"}
              className="flex-shrink-0"
            />
            <HorizontalList
              ariaLabel="Positions"
              items={props.positionTypes?.map((positionType, index) => {
                return (
                  <HStack key={index} className={"space-x-4"}>
                    <div className={"font-regular text-base"}>
                      {positionType.name}
                    </div>
                  </HStack>
                );
              })}
            />
          </HStack>
          <HStack className={"items-center space-x-4"}>
            <FiUsers className={"text-macbook flex-shrink-0"} />
            <HorizontalList
              ariaLabel="Departments"
              items={props.departments?.map((d, index) => {
                return (
                  <HStack key={index} className={"space-x-4"}>
                    <div className={"font-regular text-base"}>{d.name}</div>
                    {index !== props.positionTypes?.length - 1 &&
                    props.positionTypes?.length > 1 ? (
                      <DotDivider className={"text-macbook"} />
                    ) : null}
                  </HStack>
                );
              })}
              itemClass=""
            />
          </HStack>
          {props.closeDate && !props.isUpdate ? (
            <HStack className={"items-center space-x-4"}>
              <FiClock className={"text-macbook flex-shrink-0"} />
              <HStack className={"space-x-4"}>
                <div className={"font-regular text-base"}>
                  <span>Close Date: </span>
                  <DateTime date={new Date(props.closeDate)} />
                </div>
              </HStack>
            </HStack>
          ) : null}
        </VStack>
        <VStack className={"pt-7"}>
          <RichTextEditorBase
            content={props.description}
            readOnly
            editorClassName="no-padding"
          />
        </VStack>
        {props.allowRequestApplication ? (
          <VStack className={"space-y-3 py-4"}>
            <Tooltip
              asChild
              content={
                !isCurrentDateAfterStartDate(props.openDate)
                  ? `You will be able to apply to this job starting ${toValidLuxonDateTime(
                      props.openDate ?? "",
                    )
                      .map((dt) => dt.toFormat("MM-dd-yyyy"))
                      .orDefault("")}`
                  : ""
              }
              side="top"
            >
              <span className={"flex items-center"}>
                <Buttons.Standard
                  disabled={isApplyButtonDisabled()}
                  variant={"primary-navy"}
                  onClick={props.onRequestApplication}
                  className="w-full"
                >
                  {props.isUpdate ? "Update Application" : "Apply Now"}
                </Buttons.Standard>
              </span>
            </Tooltip>
            {props.isUpdate ? null : (
              <Buttons.Standard
                variant={"secondary"}
                onClick={props.onCopyJobLink}
              >
                Copy Job Link
              </Buttons.Standard>
            )}
          </VStack>
        ) : null}
      </VStack>
    </div>
  );
};
